import { Stepper as MantineStepper, StepperProps, useMantineTheme } from '@mantine/core';
import { forwardRef, useMemo, CSSProperties } from 'react';
import { getContrastingTextColor } from '../../../utils/color';
import classes from './Stepper.module.css';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedStepperProps extends StepperProps {
  gradientVariant?: GradientVariant;
}

interface StepperComponent extends React.ForwardRefExoticComponent<ThemedStepperProps & React.RefAttributes<HTMLDivElement>> {
  Step: typeof MantineStepper.Step;
  Completed: typeof MantineStepper.Completed;
}

const Stepper = forwardRef<HTMLDivElement, ThemedStepperProps>(
  ({ gradientVariant = 'primary', styles, classNames, children, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];

    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const cssVars = {
      '--stepper-gradient-bg': gradientBg,
      '--stepper-gradient-from': gradient.from,
      '--stepper-text-color': autoTextColor,
      '--stepper-separator-gradient': `linear-gradient(90deg, ${gradient.from}, ${gradient.to})`,
    } as CSSProperties;

    return (
      <MantineStepper
        ref={ref}
        style={cssVars}
        classNames={{
          stepIcon: classes.stepIcon,
          separator: classes.separator,
          ...classNames,
        }}
        styles={{
          ...styles,
          stepIcon: {
            ...(styles as any)?.stepIcon,
          },
          separator: {
            ...(styles as any)?.separator,
          },
        }}
        {...rest}
      >
        {children}
      </MantineStepper>
    );
  }
) as StepperComponent;

Stepper.displayName = 'Stepper';

// Attach subcomponents
Stepper.Step = MantineStepper.Step;
Stepper.Completed = MantineStepper.Completed;

export default Stepper;
